import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {IAllSpecialtyCountyData, IAvgTimeDistData, IFacilityType, IInitialData, IMaxTimeDistData, ISpecialty, ISpecialtyGroupingsData} from './types';
import {isAbortError} from '../../../../lib/error';
import {useParams, useSearchParams} from 'react-router-dom';
import {networkOptimization as noAPIs} from '../../../../lib/api';
import {NetworkContext} from '../../lib/context';
import {runInAction} from 'mobx';
import {FetchGeoJsonResponse} from '../../lib/types';

const initialApiData: IInitialData = {
    data: null,
    loading: false,
    error: null,
};

const useGetApiData = () => {
    const noStore = useContext(NetworkContext);
    const {rosterId, revisionId} = useParams();
    const [searchParams] = useSearchParams();
    const [specialtyGroupings, setSpecialtyGroupings] = useState<IInitialData<ISpecialtyGroupingsData>>(initialApiData);
    const [maxTimeDistData, setMaxTimeDistData] = useState<IInitialData<IMaxTimeDistData>>(initialApiData);
    const [providerAvgTimeDistData, setProviderAvgTimeDistData] = useState<IInitialData<IAvgTimeDistData>>(initialApiData);
    const [facilityAvgTimeDistData, setFacilityAvgTimeDistData] = useState<IInitialData<IAvgTimeDistData>>(initialApiData);
    const [allSpecialtyCountyData, setAllSpecialtyCountyData] = useState<IInitialData<IAllSpecialtyCountyData>>(initialApiData);

    // Load geography filters data (needed for county FIPS codes in boundary maps)
    useEffect(() => {
        if (rosterId && noStore.availableGeographyFilters.length === 0 && !noStore.loadingGeographyData) {
            runInAction(() => {
                noStore.runId = +rosterId;
            });
            noStore.loadGeographyData();
        }
    }, [rosterId, noStore.loadingGeographyData]);

    // Parse URL parameters directly - they're already in API payload format
    const urlParams = useMemo(() => ({
        standard_type: searchParams.get('standard_type') || '',
        standard_version: searchParams.get('standard_version') || '',
        patient_roster_id: searchParams.get('patient_roster_id') || '',
        state: searchParams.get('state') || '',
        county: searchParams.get('county') || '',
        specialty_filter_code: searchParams.get('specialty_filter_code') || '',
    }), [searchParams]);

    const allSpecialties = useMemo(() => {
        const {providerSpecialties = [], facilitySpecialties = []} = specialtyGroupings.data || {};
        return [...providerSpecialties, ...facilitySpecialties];
    }, [specialtyGroupings.data]);

    const specialtyCodes = useMemo(() => {
        if (urlParams.specialty_filter_code) {
            return urlParams.specialty_filter_code.split('|');
        }
        return allSpecialties.length > 0 ? allSpecialties.map(s => s.specialty_code) : [];
    }, [urlParams.specialty_filter_code, allSpecialties]);

    const fetchSpecialtyGroupings = useCallback(async (signal: AbortSignal) => {
        if (!rosterId) return;
        if (!urlParams.standard_type || !urlParams.standard_version) return;

        try {
            setSpecialtyGroupings({...initialApiData, loading: true});
            const {data} = await noAPIs.fetch_specialty_grouping({
                roster_id: +rosterId,
                standard_type: urlParams.standard_type,
                standard_version: urlParams.standard_version,
                specialty_code: urlParams.specialty_filter_code ? urlParams.specialty_filter_code.split('|') : [],
                signal
            }) as {data: ISpecialty[]};

            const specialtyGroupings: ISpecialtyGroupingsData = {providerSpecialties: [], facilitySpecialties: []};
            data.forEach(sg => {
                if (sg.provider_facility_type === 'Provider') {
                    specialtyGroupings.providerSpecialties.push(sg);
                } else if (sg.provider_facility_type === 'Facility') {
                    specialtyGroupings.facilitySpecialties.push(sg);
                }
            });
            setSpecialtyGroupings({data: specialtyGroupings, error: null, loading: false});
        } catch (error) {
            if (isAbortError(error as Error)) return;
            setSpecialtyGroupings({data: null, loading: false, error: (error as Error).message});
        }
    }, [rosterId, urlParams.specialty_filter_code, urlParams.standard_type, urlParams.standard_version]);

    const fetchMaxTimeDistData = useCallback(async (signal: AbortSignal) => {
        if (!rosterId || !revisionId) return;
        if (!urlParams.standard_type || !urlParams.standard_version) return;

        try {
            setMaxTimeDistData({...initialApiData, loading: true});
            const {data} = await noAPIs.fetch_max_time_and_distance({
                roster_id: +rosterId,
                revision_id: +revisionId,
                standard_type: urlParams.standard_type,
                standard_version: urlParams.standard_version,
                specialty_code: urlParams.specialty_filter_code ? urlParams.specialty_filter_code.split('|') : [],
                signal
            });
            setMaxTimeDistData({data, error: null, loading: false});
        } catch (error) {
            if (isAbortError(error as Error)) return;
            setMaxTimeDistData({data: null, loading: false, error: (error as Error).message});
        }
    }, [rosterId, revisionId, urlParams.standard_type, urlParams.standard_version, urlParams.specialty_filter_code]);

    const fetchAvgTimeDistData = useCallback(async (signal: AbortSignal, type: IFacilityType) => {
        if (!rosterId || !revisionId) return;
        if (!urlParams.standard_type || !urlParams.standard_version) return;

        const stateSetter = type === 'Provider' ? setProviderAvgTimeDistData : setFacilityAvgTimeDistData;
        try {
            stateSetter({...initialApiData, loading: true});
            const {data} = await noAPIs.fetch_avg_time_and_distance({
                roster_id: +rosterId,
                revision_id: +revisionId,
                standard_type: urlParams.standard_type,
                standard_version: urlParams.standard_version,
                provider_facility_type: type,
                specialty_code: urlParams.specialty_filter_code ? urlParams.specialty_filter_code.split('|') : [],
                signal
            });
            stateSetter({data, error: null, loading: false});
        } catch (error) {
            if (isAbortError(error as Error)) return;
            stateSetter({data: null, loading: false, error: (error as Error).message});
        }
    }, [rosterId, revisionId, urlParams.standard_type, urlParams.standard_version, urlParams.specialty_filter_code]);

    const fetchProviderGeoJson = useCallback(async (signal: AbortSignal) => {
        if (!rosterId || !revisionId) return;
        if (!urlParams.standard_type || !urlParams.standard_version) return;

        try {
            runInAction(() => {
                noStore.reportProviderGeoJsonLoading = true;
                noStore.reportProviderGeoJsonError = null;
            });

            // Split specialty codes into chunks of 10 to avoid gateway timeout
            const BATCH_SIZE = 10;
            const MAX_RETRIES = 2; // 1 initial attempt + 2 retries = 3 total attempts
            const RETRY_DELAY_MS = 1000; // Start with 1 second delay

            const specialtyChunks: string[][] = [];
            for (let i = 0; i < specialtyCodes.length; i += BATCH_SIZE) {
                specialtyChunks.push(specialtyCodes.slice(i, i + BATCH_SIZE));
            }

            const providerDataBySpecialty: Record<string, FetchGeoJsonResponse> = {};

            // Fetch chunks sequentially to avoid overwhelming the server
            for (let i = 0; i < specialtyChunks.length; i++) {
                const chunk = specialtyChunks[i];

                const filters: Record<string, any> = {
                    state: urlParams.state,
                    county: urlParams.county,
                    standard_type: urlParams.standard_type,
                    standard_version: urlParams.standard_version,
                    specialty_code: chunk
                };

                let retryCount = 0;
                let success = false;

                // Retry logic with exponential backoff
                while (retryCount <= MAX_RETRIES && !success) {
                    try {
                        const response = await noAPIs.fetchProviderGeoJsonBatch(
                            filters,
                            +rosterId,
                            +revisionId,
                            signal
                        );

                        // Merge response into accumulated results
                        if (response?.data) {
                            for (const [specialtyCode, geoJson] of Object.entries(response.data)) {
                                const featureCollection = geoJson as FetchGeoJsonResponse;
                                // Always add the specialty, even if empty - this lets us distinguish
                                // between "not loaded yet" vs "loaded but has no providers"
                                providerDataBySpecialty[specialtyCode] = featureCollection || {
                                    type: 'FeatureCollection',
                                    features: []
                                };
                            }
                        }

                        success = true;

                        // Progressively update store after each successful batch
                        // This allows maps to start rendering immediately with available data
                        runInAction(() => {
                            (noStore as any)._reportProviderGeoJson = {...providerDataBySpecialty};
                            noStore.reportProviderGeoJsonLoading = i < specialtyChunks.length - 1;
                        });
                    } catch (error) {
                        if (isAbortError(error as Error)) {
                            throw error; // Don't retry if request was aborted
                        }

                        retryCount++;
                        if (retryCount > MAX_RETRIES) {
                            console.error(`Batch ${i + 1}/${specialtyChunks.length} failed after ${MAX_RETRIES} retries:`, error);
                            throw new Error('Unable to load provider location data. Please try refreshing the page or contact support if the issue persists.');
                        }

                        // Exponential backoff: wait longer before each retry
                        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount - 1);
                        console.warn(`Batch ${i + 1}/${specialtyChunks.length} failed, retrying in ${delay}ms (attempt ${retryCount}/${MAX_RETRIES})`);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
            }
        } catch (error) {
            if (isAbortError(error as Error)) return;
            runInAction(() => {
                noStore.reportProviderGeoJsonError = (error as Error).message;
                noStore.reportProviderGeoJsonLoading = false;
                noStore.showToastNotification('error', 'Error Loading Provider Data', (error as Error).message);
            });
        }
    }, [rosterId, revisionId, urlParams.state, urlParams.county, urlParams.standard_type, urlParams.standard_version, specialtyCodes, noStore]);

    useEffect(() => {
        if (!urlParams.standard_type || !urlParams.standard_version) return;
        const abortController = new AbortController();
        const promises = [
            fetchSpecialtyGroupings(abortController.signal),
            fetchMaxTimeDistData(abortController.signal),
            fetchAvgTimeDistData(abortController.signal, 'Provider'),
            fetchAvgTimeDistData(abortController.signal, 'Facility')
        ];
        Promise.all(promises);
        return () => abortController.abort();
    }, [fetchSpecialtyGroupings,
        fetchMaxTimeDistData,
        fetchAvgTimeDistData]);

    useEffect(() => {
        if (!rosterId || !revisionId || !allSpecialties.length) return;
        const abortController = new AbortController();
        (async () => {
            try {
                setAllSpecialtyCountyData({...initialApiData, loading: true});
                const {data, errors: dataErrors = null} = await noAPIs.fetch_specialty_stds_by_county_batch({
                    roster_id: +rosterId,
                    revision_id: +revisionId,
                    standard_type: urlParams.standard_type,
                    standard_version: urlParams.standard_version,
                    signal: abortController.signal,
                    specialty_code: specialtyCodes,
                    state: urlParams.state ? urlParams.state.split('|') : [],
                    county: urlParams.county ? urlParams.county.split('|') : [],
                });
                setAllSpecialtyCountyData({data: {data, dataErrors}, error: null, loading: false});
            } catch (error) {
                if (isAbortError(error as Error)) return;
                setAllSpecialtyCountyData({data: null, loading: false, error: (error as Error).message});
            }
        })();
        return () => abortController.abort();
    }, [rosterId, revisionId, urlParams.state, urlParams.county, urlParams.standard_type, urlParams.standard_version, specialtyCodes, allSpecialties.length]);

    // Load provider GeoJSON data for maps
    // Only fetch when specialty groupings are loaded
    useEffect(() => {
        // Wait for specialty groupings to be loaded before fetching provider GeoJSON
        if (!specialtyGroupings.data && specialtyGroupings.loading) {
            return; // Still loading specialty groupings, wait
        }

        const abortController = new AbortController();
        fetchProviderGeoJson(abortController.signal);
        return () => abortController.abort();
    }, [fetchProviderGeoJson, specialtyGroupings.data, specialtyGroupings.loading]);

    return {
        data: {
            specialtyGroupings,
            maxTimeDistData,
            providerAvgTimeDistData,
            facilityAvgTimeDistData,
            allSpecialties,
            allSpecialtyCountyData
        }
    };
};

export default useGetApiData;
